import React, { useRef } from 'react';
import { calculateRisk, getRiskLevel } from '../utils/securityLogic';
import SafeIcon from '../common/SafeIcon';
import VEITLogo from './VEITLogo';
import RadarChart from './report/RadarChart';
import ThreatMatrix from './report/ThreatMatrix';
import RecommendationCard from './report/RecommendationCard';
import {
  FiPrinter, FiArrowLeft, FiAlertTriangle, FiCheckCircle,
  FiAlertCircle, FiXCircle, FiShield, FiCalendar, FiUser, FiBriefcase
} from 'react-icons/fi';

export const RiskBadge = ({ score, size = 'sm' }) => {
  const level = getRiskLevel(score);
  const icons = { CRITICAL: FiXCircle, HIGH: FiAlertTriangle, MEDIUM: FiAlertCircle, LOW: FiCheckCircle, MINIMAL: FiCheckCircle };
  const Icon = icons[level.label] || FiAlertCircle;
  const sz = size === 'lg' ? 'text-xs px-3 py-1.5' : 'text-[10px] px-2 py-0.5';
  return (
    <span className={`inline-flex items-center gap-1 font-bold rounded-full uppercase tracking-wider ${sz} ${level.color} ${level.bg}`}>
      <SafeIcon icon={Icon} className="text-[10px]" /> {level.label}
    </span>
  );
};

const ScoreRing = ({ score }) => {
  const level = getRiskLevel(score);
  const ringColors = { CRITICAL: '#dc2626', HIGH: '#ea580c', MEDIUM: '#ca8a04', LOW: '#2F6FED', MINIMAL: '#16a34a' };
  const color = ringColors[level.label] || '#2F6FED'; // Fallback to veit-secondary
  const pct = (parseFloat(score) / 5) * 100;
  const r = 40, circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  
  return (
    <div className="relative w-40 h-40 flex items-center justify-center">
      <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} fill="none" stroke="#F5F7FA" strokeWidth="9" />
        <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="9"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 6px ${color}55)` }} />
      </svg>
      <div className="text-center z-10">
        <div className="text-4xl font-black text-veit-navy leading-none">{score}</div>
        <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">out of 5.0</div>
        <div className="mt-2"><RiskBadge score={score} size="lg" /></div>
      </div>
    </div>
  );
};

const MetaItem = ({ icon, label, value }) => (
  <div className="flex items-center gap-2">
    <SafeIcon icon={icon} className="text-veit-secondary text-xs shrink-0" />
    <span className="text-slate-300 text-xs mr-1">{label}</span>
    <span className="text-white text-xs font-semibold">{value || 'N/A'}</span>
  </div>
);

const SectionHeader = ({ num, title, subtitle }) => (
  <div className="flex items-start gap-3 mb-5">
    <div className="flex flex-col items-center">
      <div className="w-8 h-8 rounded-lg bg-veit-secondary flex items-center justify-center shrink-0">
        <span className="text-white text-[10px] font-black">{num}</span>
      </div>
    </div>
    <div className="flex-1 border-b border-veit-border pb-3">
      <h2 className="text-base font-black text-veit-navy uppercase tracking-wide">{title}</h2>
      {subtitle && <p className="text-xs text-veit-gray mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

const DomainRow = ({ domain, index }) => {
  const level = getRiskLevel(domain.score);
  const barColors = {
    CRITICAL: 'from-red-500 to-red-400', HIGH: 'from-orange-500 to-amber-400',
    MEDIUM: 'from-yellow-500 to-yellow-400', LOW: 'from-veit-primary to-veit-secondary', MINIMAL: 'from-green-500 to-emerald-400'
  };
  return (
    <div className={`grid grid-cols-[1.6fr_80px_100px_2fr] gap-3 items-center py-3 px-4 text-[12px] ${index % 2 === 0 ? 'bg-white' : 'bg-veit-light'}`}>
      <div className="font-semibold text-veit-navy">{domain.name}</div>
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <span className="font-black text-veit-navy">{domain.score.toFixed(1)}</span>
        </div>
        <div className="h-1.5 bg-veit-border rounded-full overflow-hidden">
          <div className={`h-full rounded-full bg-gradient-to-r ${barColors[level.label]}`}
            style={{ width: `${(domain.score / 5) * 100}%` }} />
        </div>
      </div>
      <div><RiskBadge score={domain.score} /></div>
      <div className="text-veit-gray leading-snug">{domain.finding}</div>
    </div>
  );
};

const ReportView = ({ data, onBack }) => {
  const reportData = calculateRisk(data);
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const reportId = `VE-${Date.now().toString(36).toUpperCase().slice(-6)}`;

  return (
    <div className="min-h-screen bg-veit-light py-8 print:py-0 print:bg-white flex flex-col items-center">
      {/* Toolbar */}
      <div className="w-full max-w-[960px] flex justify-between items-center mb-5 print:hidden px-4">
        <button onClick={onBack}
          className="flex items-center gap-2 text-veit-gray hover:text-veit-navy text-sm font-semibold bg-white px-4 py-2 rounded-lg shadow-sm border border-veit-border transition-all hover:shadow-md">
          <SafeIcon icon={FiArrowLeft} /> Back to Intake
        </button>
        <button onClick={() => window.print()}
          className="flex items-center gap-2 bg-veit-secondary hover:bg-veit-primary text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all shadow-md hover:shadow-veit-secondary/30 hover:-translate-y-0.5">
          <SafeIcon icon={FiPrinter} /> Export PDF Report
        </button>
      </div>

      {/* Report Document */}
      <div className="bg-white w-full max-w-[960px] shadow-2xl print:shadow-none print:max-w-none overflow-hidden rounded-2xl print:rounded-none">

        {/* ── COVER HEADER ── */}
        <div className="bg-gradient-to-br from-veit-navy via-[#0c2238] to-veit-dark relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-veit-primary/20 rounded-full -translate-y-1/2 translate-x-1/3 blur-3xl" />
          <div className="absolute bottom-0 left-1/3 w-64 h-64 bg-veit-secondary/20 rounded-full translate-y-1/2 blur-2xl" />
          <div className="absolute inset-0 opacity-[0.03]"
            style={{ backgroundImage: 'repeating-linear-gradient(0deg,#fff 0,#fff 1px,transparent 1px,transparent 40px),repeating-linear-gradient(90deg,#fff 0,#fff 1px,transparent 1px,transparent 40px)' }} />

          <div className="relative px-10 pt-8 pb-6">
            {/* Top bar */}
            <div className="flex justify-between items-start mb-8">
              <VEITLogo light />
              <div className="text-right">
                <div className="text-[10px] text-veit-highlight uppercase tracking-widest opacity-80">Report ID</div>
                <div className="text-white font-mono font-bold text-sm">{reportId}</div>
              </div>
            </div>

            {/* Title block */}
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 bg-veit-secondary/20 border border-veit-secondary/40 text-veit-highlight text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-3">
                <SafeIcon icon={FiShield} className="text-[10px]" />
                Discovery Assessment — Confidential
              </div>
              <h1 className="text-3xl font-black text-white tracking-tight leading-tight">
                EdgeCheck Security<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-veit-highlight to-veit-secondary">Readiness Report</span>
              </h1>
            </div>

            {/* Meta grid */}
            <div className="grid grid-cols-2 gap-x-10 gap-y-2 border-t border-white/10 pt-5">
              <MetaItem icon={FiBriefcase} label="Client" value={data.companyName} />
              <MetaItem icon={FiCalendar} label="Date" value={today} />
              <MetaItem icon={FiShield} label="Industry" value={data.industry} />
              <MetaItem icon={FiUser} label="Contact" value={data.primaryContact} />
              <MetaItem icon={FiUser} label="Employees" value={data.employeeCount} />
              <MetaItem icon={FiShield} label="Scope" value="12-Question Intake" />
            </div>
          </div>

          {/* Score strip */}
          <div className="bg-white/5 border-t border-white/10 px-10 py-4 flex items-center justify-between relative z-10">
            <div className="flex items-center gap-6">
              {[{ v: '6', l: 'Domains Assessed' }, { v: '12', l: 'Questions' }, { v: reportData.overallScore, l: 'Overall Score' }].map(s => (
                <div key={s.l} className="text-center">
                  <div className="text-2xl font-black text-white">{s.v}</div>
                  <div className="text-[9px] text-veit-highlight opacity-80 uppercase tracking-widest">{s.l}</div>
                </div>
              ))}
            </div>
            <RiskBadge score={reportData.overallScore} size="lg" />
          </div>
        </div>

        <div className="px-10 py-8 space-y-10 text-sm">

          {/* ── SECTION 1: EXECUTIVE SUMMARY ── */}
          <section>
            <SectionHeader num="01" title="Executive Summary"
              subtitle="High-level risk posture based on intake responses" />
            <div className="flex gap-8 items-start">
              <div className="shrink-0 flex flex-col items-center gap-3">
                <ScoreRing score={reportData.overallScore} />
                <div className="text-[10px] text-veit-gray text-center uppercase tracking-wider font-semibold">Security Posture<br />Score</div>
              </div>
              <div className="flex-1">
                <div className="bg-gradient-to-br from-veit-light to-veit-highlight/40 border border-veit-border rounded-xl p-5">
                  <p className="text-veit-gray font-medium leading-relaxed text-[13.5px]">{reportData.summary}</p>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {reportData.domains.filter(d => getRiskLevel(d.score).label === 'CRITICAL' || getRiskLevel(d.score).label === 'HIGH').slice(0, 3).map(d => (
                    <div key={d.id} className="bg-red-50 border border-red-100 rounded-xl p-3">
                      <div className="text-[9px] font-bold text-red-500 uppercase tracking-widest mb-1">High Priority</div>
                      <div className="text-xs font-bold text-red-800">{d.name}</div>
                      <div className="text-[10px] text-red-600 mt-0.5 font-semibold">Score: {d.score.toFixed(1)} / 5.0</div>
                    </div>
                  ))}
                  {reportData.domains.filter(d => getRiskLevel(d.score).label === 'MINIMAL' || getRiskLevel(d.score).label === 'LOW').slice(0, 3 - reportData.domains.filter(d => ['CRITICAL','HIGH'].includes(getRiskLevel(d.score).label)).length).map(d => (
                    <div key={d.id} className="bg-green-50 border border-green-100 rounded-xl p-3">
                      <div className="text-[9px] font-bold text-green-600 uppercase tracking-widest mb-1">Strength</div>
                      <div className="text-xs font-bold text-green-800">{d.name}</div>
                      <div className="text-[10px] text-green-600 mt-0.5 font-semibold">Score: {d.score.toFixed(1)} / 5.0</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ── SECTION 2: VISUAL RISK OVERVIEW ── */}
          <section>
            <SectionHeader num="02" title="Visual Risk Overview"
              subtitle="Radar chart and threat matrix across all six security domains" />
            <div className="grid grid-cols-2 gap-6">
              <RadarChart domains={reportData.domains} />
              <ThreatMatrix domains={reportData.domains} />
            </div>
          </section>

          {/* ── SECTION 3: DOMAIN SCORECARD ── */}
          <section>
            <SectionHeader num="03" title="Risk Domain Scorecard"
              subtitle="Detailed per-domain findings and risk ratings" />
            <div className="border border-veit-border rounded-xl overflow-hidden">
              <div className="grid grid-cols-[1.6fr_80px_100px_2fr] gap-3 px-4 py-2.5 bg-veit-navy text-[10px] font-bold text-veit-highlight uppercase tracking-wider">
                <div>Domain</div>
                <div>Score</div>
                <div>Risk Level</div>
                <div>Key Finding</div>
              </div>
              {reportData.domains.map((domain, idx) => (
                <DomainRow key={domain.id} domain={domain} index={idx} />
              ))}
            </div>
          </section>

          {/* ── SECTION 4: RECOMMENDATIONS ── */}
          <section>
            <SectionHeader num="04" title="Top Priority Recommendations"
              subtitle="Ranked by risk impact — address these first" />
            <div className="space-y-4">
              {reportData.recommendations.map((rec, idx) => (
                <RecommendationCard key={idx} rec={rec} index={idx} />
              ))}
            </div>
          </section>

          {/* ── SECTION 5: NEXT STEPS ── */}
          <section>
            <SectionHeader num="05" title="VEIT Recommended Next Steps"
              subtitle="Your path forward with Visual Edge IT" />
            <div className="bg-gradient-to-br from-veit-navy via-[#0c2238] to-veit-dark rounded-2xl overflow-hidden relative">
              <div className="absolute top-0 right-0 w-64 h-64 bg-veit-secondary/10 rounded-full -translate-y-1/3 translate-x-1/3 blur-3xl" />
              <div className="relative p-6">
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {[
                    { step: '01', title: 'EdgeCheck Review', desc: '60-min findings walkthrough with a VEIT vCISO', color: 'border-veit-secondary/40 bg-veit-secondary/10' },
                    { step: '02', title: 'Technical Assessment', desc: 'Validated vulnerability scan & full gap analysis', color: 'border-cyan-500/40 bg-cyan-600/10' },
                    { step: '03', title: 'Remediation Roadmap', desc: 'Prioritized 90-day security improvement plan', color: 'border-veit-primary/40 bg-veit-primary/20' },
                  ].map(s => (
                    <div key={s.step} className={`border rounded-xl p-4 ${s.color}`}>
                      <div className="text-[10px] font-black text-veit-highlight uppercase tracking-widest mb-1 opacity-80">Step {s.step}</div>
                      <div className="text-white font-bold text-sm mb-1">{s.title}</div>
                      <div className="text-veit-highlight text-[11px] leading-snug font-medium opacity-90">{s.desc}</div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-white/10 pt-4 flex items-center justify-between">
                  <div>
                    <p className="text-veit-highlight text-[13px] leading-relaxed opacity-90">
                      Ready to remediate? Contact your <strong className="text-white">VEIT Account Executive</strong> or visit{' '}
                      <a href="https://www.visualedgeit.com" className="text-white underline underline-offset-2 hover:text-veit-secondary transition-colors">
                        www.visualedgeit.com
                      </a>
                    </p>
                  </div>
                  <VEITLogo light className="opacity-90 shrink-0 ml-6 scale-90 origin-right" />
                </div>
              </div>
            </div>
          </section>

          {/* Disclaimer */}
          <div className="border-t border-veit-border pt-5 flex gap-4 items-start">
            <div className="w-6 h-6 rounded-full bg-veit-light flex items-center justify-center shrink-0">
              <SafeIcon icon={FiAlertCircle} className="text-veit-gray text-xs" />
            </div>
            <p className="text-[10px] text-veit-gray italic leading-relaxed font-medium">
              <strong className="not-italic text-veit-navy">Disclaimer:</strong> This EdgeCheck report is a discovery-level assessment based on client-provided intake information. It is not a penetration test, vulnerability scan, or compliance audit. Findings represent risk indicators only and should not be construed as a complete security evaluation. Visual Edge IT recommends a full EdgeCheck Technical Assessment for validated results. Report ID: {reportId}.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportView;